import {
  Dimensions,
  MarketplaceOrderApiModel,
  ShipmentPlanApiModel,
  ShipmentPlanParcelApiModel,
  ShipmentPlanParcelItemApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { SmartSendItemGroup } from '../item/SmartSendGroup';
import { FilterToSendableItems } from '../panels/FilterToSendableItems';
import { SmartSendProvider } from '~/lib/consts/providers';
import { clearLocalUnitsEntryFields } from '~/apps/smartsend/lib/helpers/DimensionUnits';
import { getLocalUnitsEntry, reconcileLocalUnitsEntry } from '~/apps/smartsend/lib/helpers/LocalUnitsEntryHelper';

interface SplitSummary {
  item: SmartSendItemApiModel;
  newItems: SmartSendItemApiModel[];
}

interface CombiningSummary {
  items: SmartSendItemApiModel[];
  newItem: SmartSendItemApiModel;
}

// Builds an order line for one box's content row from the single original line this SPLIT flow
// started from, carrying through whatever the user edited (or the auto-computed value split, if
// untouched - both live in `content` since Splitting.tsx stores them in the same parcel state).
// `nullRefs` is true for manual rows (never had real refs) and for every row on boxes 2..N (new items).
export function BuildSplitOrderLine(
  content: ShipmentPlanParcelItemApiModel,
  baseLine: MarketplaceOrderApiModel,
  nullRefs: boolean
): MarketplaceOrderApiModel {
  const line: MarketplaceOrderApiModel = JSON.parse(JSON.stringify(baseLine));
  line.Quantity = content.Quantity;
  line.Value = content.Value;
  line.Product = {
    ...line.Product,
    Description: content.Description,
    Sku: content.Sku,
    TariffCode: content.TariffCode,
    CountryOfManufacture: content.CountryOfManufacture
  };

  if (nullRefs) {
    line.Id = null;
    line.MarketplaceOrderId = null;
    line.MarketplaceOrderLineId = null;
    line.CustomerReference = null;
    line.InternalReference = null;
    line.OrderLink = null;
    line.Source = { ...line.Source, Id: 'manual' };
  }

  return line;
}

export function GenerateSplitSummary(
  group: SmartSendItemGroup,
  split: number,
  parcels: ShipmentPlanParcelApiModel[],
  dims: Dimensions
): SplitSummary {
  const existing: SmartSendItemApiModel = JSON.parse(JSON.stringify(group.Items[0]));
  const newItems = [];

  if (split > 1 && existing.MarketplaceStatus === 'Sold') {
    const baseLine = existing.MarketplaceOrderLines[0];

    // Add Count Tag
    existing.Tags.push(`Box 1 of ${split}`);

    // Update First Item Desc + Contents
    existing.Description = parcels[0]?.Contents[0]?.Description ?? existing.Description;
    existing.MarketplaceOrderLines = (parcels[0]?.Contents ?? []).map((c) =>
      BuildSplitOrderLine(c, baseLine, !!c.Manual)
    );

    for (let index = 1; index < split; index++) {
      // Add Subsequent Boxes
      const newItemRequest: SmartSendItemApiModel = JSON.parse(JSON.stringify(group.Items[0]));
      newItemRequest.Id = null;
      newItemRequest.Source.Id = 'manual';
      newItemRequest.Tags.push(`Box ${index + 1} of ${split}`);
      newItemRequest.PackageDimensions = { ...dims };
      newItemRequest.Description = parcels[index]?.Contents[0]?.Description ?? newItemRequest.Description;
      newItemRequest.MarketplaceOrderLines = (parcels[index]?.Contents ?? []).map((c) =>
        BuildSplitOrderLine(c, baseLine, true)
      );
      newItems.push(newItemRequest);
    }

    return { item: existing, newItems: newItems };
  }
}

/**
 * Applies one box's derived weight to a converted item.
 *
 * L/W/H is left as the original item's: Convert never asks for a box size, so the original box is the only
 * honest default. Same convention as SplitConsignmentWeight and useParcelPlan.applyDimensions - copy the
 * dimensions, divide the weight.
 *
 * The as-entered weight original has to go with it. Each output is a deep clone carrying the source item's
 * Meta['LocalUnitsEntry'], whose originalWeightLb describes the WHOLE order - so an imperial customer would
 * be shown 22lb against an 11lb box. Guarded on the weight actually changing, and the length/width/height
 * originals are kept because those values are unchanged.
 */
function applyBoxWeight(item: SmartSendItemApiModel, weight: number) {
  if (!(weight > 0) || weight === item.PackageDimensions?.Weight) {
    return;
  }

  item.PackageDimensions = { ...item.PackageDimensions, Weight: weight };

  const entry = getLocalUnitsEntry(item.Meta);
  if (entry?.originalWeightLb != null) {
    // reconcile rather than set: if the weight was the only as-entered value, the record should go rather
    // than linger blank.
    reconcileLocalUnitsEntry(item.Meta, clearLocalUnitsEntryFields(entry, ['weight']));
  }
}

export function GenerateQuantityConvertedSummary(
  group: SmartSendItemGroup,
  boxes: { quantity: number; value: number; weight: number }[]
): SplitSummary {
  const existing = JSON.parse(JSON.stringify(group.Items[0])) as SmartSendItemApiModel;

  const newItems = [];

  if (existing.MarketplaceStatus === 'Sold') {
    existing.MarketplaceOrderLines.forEach((o) => {
      o.Quantity = boxes[0].quantity;
      o.Value = boxes[0].value;
    });
    // Without this every output kept the ORIGINAL total weight, so splitting a 10kg quantity-4 item in two
    // quoted and booked two 10kg shipments.
    applyBoxWeight(existing, boxes[0].weight);

    for (let index = 1; index < boxes.length; index++) {
      // Add Subsequent Boxes
      const newItemRequest = JSON.parse(JSON.stringify(group.Items[0])) as SmartSendItemApiModel;
      newItemRequest.Id = null;
      newItemRequest.Source.Id = 'manual';
      newItemRequest.MarketplaceOrderLines.forEach((x) => {
        x.Id = null;
        x.MarketplaceOrderId = null;
        x.MarketplaceOrderLineId = null;
        x.CustomerReference = null;
        x.InternalReference = null;
        x.OrderLink = null;
        x.Source.Id = 'manual';
        x.Quantity = boxes[index].quantity;
        x.Value = boxes[index].value;
      });
      newItemRequest.Tags = newItemRequest.Tags.filter((x) => x.indexOf('Box ') === -1);
      applyBoxWeight(newItemRequest, boxes[index].weight);

      newItems.push(newItemRequest);
    }

    return { item: existing, newItems: newItems };
  }
}

export function GenerateCombiningSummary(group: SmartSendItemGroup, dims: Dimensions): CombiningSummary {
  const req: SmartSendItemApiModel[] = [];

  // New Item will all the parcels
  const newItemRequest: SmartSendItemApiModel = JSON.parse(JSON.stringify(group.Items[0]));
  newItemRequest.Id = null;
  newItemRequest.Source = { Id: 'grouped', Name: 'Grouped', AccountId: SmartSendProvider };
  newItemRequest.MarketplaceOrderLines = [];
  newItemRequest.PackageDimensions = { ...dims };
  newItemRequest.Tags = newItemRequest.Tags.filter((x) => x.indexOf('Box ') === -1);

  // All the empty boxes
  group.Items.forEach((i) => {
    newItemRequest.MarketplaceOrderLines = newItemRequest.MarketplaceOrderLines.concat(
      i.MarketplaceOrderLines.filter(FilterToSendableItems)
    );
    req.push({ ...i, MarketplaceOrderLines: [] });
  });

  return { items: req.concat([newItemRequest]), newItem: newItemRequest };
}

// Builds one output SmartSendItem per parcel in `state` (the arrangement the user built in the
// parcel arranger). Each parcel's content rows are resolved back to their original order line across
// the WHOLE group (not just group.Items[0]) - a line can have come from any group item, so every
// group item's sendable lines are searched, not just the first item's.
//
// Every row goes through BuildSplitOrderLine, the same path GenerateSplitSummary uses. Previously
// this returned the matched order line verbatim, which had three consequences: per-row customs edits
// were silently discarded, manually added rows (Id: null) matched nothing and vanished, and two rows
// sharing an Id resolved to the SAME object twice so mutating one mutated the other.
export function GenerateCustomSplitItems(
  group: SmartSendItemGroup,
  state: ShipmentPlanApiModel,
  dims: Dimensions
): SmartSendItemApiModel[] {
  const output: SmartSendItemApiModel[] = [];

  const base = JSON.parse(JSON.stringify(group.Items[0])) as SmartSendItemApiModel;

  const allLines = group.Items.flatMap((item) => item.MarketplaceOrderLines.filter(FilterToSendableItems));

  state.Parcels.forEach((p) => {
    output.push({
      ...base,
      Id: p.Id,
      Source: group.Items.find((x) => x.Id === p.Id)?.Source ||
        allLines.find((m) => m.Id === p.Id)?.Source || { Name: 'Manual', Id: 'manual' },
      MarketplaceOrderLines: p.Contents.map((content) => {
        const line = allLines.find((m) => m.Id === content.Id);
        // A manual row has no line of its own, so it borrows one for its shape and then has every
        // marketplace reference nulled - it is a brand new line, not a moved one.
        const template = line ?? allLines[0] ?? base.MarketplaceOrderLines[0];
        return template ? BuildSplitOrderLine(content, template, !line) : null;
      }).filter((x) => !!x),
      PackageDimensions: { ...dims, ...p.Dimensions }
    } as SmartSendItemApiModel);
  });

  group.Items.forEach((x) => {
    if (!output.find((o) => o.Id === x.Id)) {
      output.push({ ...x, MarketplaceOrderLines: [] });
    }
  });

  return output;
}
